import { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import { Chip, MenuItem, Select, Typography } from "@mui/material";
import { Download } from "@mui/icons-material";

import {
  SURVEY_RESPONSES_EXPORT_ENDPOINT,
  SURVEY_RESPONSES_LIST_ENDPOINT,
  SURVEY_RESPONSES_SUMMARY_ENDPOINT,
} from "../../config/urls";
import { useAuth } from "../../hooks/AuthProvider";
import { ColorsConstants } from "../../cssUtils/constants";
import typography from "../../cssUtils/typography";
import ButtonOne from "../../common/normal-button";
import AppTable from "../../common/app-table";
import TableSearchInput from "../TableSearchInput";

const columns = [
  { id: "respondent", label: "Respondent" },
  { id: "submitted_at", label: "Submitted" },
  { id: "current_behaviour", label: "Current Behaviour (Q1)" },
  { id: "satisfaction", label: "Satisfaction (Q5)" },
  { id: "status", label: "Status" },
];

export default function ResponsesTable() {
  const { getValidToken } = useAuth();

  const [responses, setResponses] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [controller, setController] = useState({ page: 0, rowsPerPage: 20 });

  const [search, setSearch] = useState("");
  const [charCount, setCharCount] = useState(0);
  const [behaviourFilter, setBehaviourFilter] = useState("");
  const [satisfactionFilter, setSatisfactionFilter] = useState("");

  const [behaviourOptions, setBehaviourOptions] = useState([]);
  const [satisfactionOptions, setSatisfactionOptions] = useState([]);

  const authHeaders = async () => {
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };
  };

  // Filter option lists come from the (unfiltered) summary endpoint once, so they
  // can never drift out of sync with whatever answer text is actually in the data.
  useEffect(() => {
    (async () => {
      try {
        const headers = await authHeaders();
        const response = await fetch(SURVEY_RESPONSES_SUMMARY_ENDPOINT, { headers });
        if (!response.ok) return;
        const data = await response.json();
        setBehaviourOptions(data.questions?.current_behaviour || []);
        setSatisfactionOptions(data.questions?.satisfaction || []);
      } catch (error) {
        console.error("Error fetching survey summary options:", error.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildParams = () =>
    new URLSearchParams({
      limit: controller.rowsPerPage.toString(),
      offset: (controller.page * controller.rowsPerPage).toString(),
      search,
      current_behaviour: behaviourFilter,
      satisfaction: satisfactionFilter,
    });

  const fetchData = async () => {
    try {
      const headers = await authHeaders();
      const response = await fetch(`${SURVEY_RESPONSES_LIST_ENDPOINT}?${buildParams()}`, { headers });
      if (!response.ok) throw new Error("Failed to fetch survey responses");
      const data = await response.json();
      setResponses(data.results || []);
      setRowCount(data.count || 0);
    } catch (error) {
      console.error("Error fetching survey responses:", error.message);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(fetchData, 250); // debounce so typing doesn't fire a request per keystroke
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controller.page, controller.rowsPerPage, search, behaviourFilter, satisfactionFilter]);

  const updateSearch = (value) => {
    setSearch(value);
    setCharCount(value.length);
    setController({ page: 0, rowsPerPage: 20 });
  };

  const handleExport = async () => {
    try {
      const headers = await authHeaders();
      const params = new URLSearchParams({
        search,
        current_behaviour: behaviourFilter,
        satisfaction: satisfactionFilter,
      });
      const response = await fetch(`${SURVEY_RESPONSES_EXPORT_ENDPOINT}?${params}`, { headers });
      if (!response.ok) throw new Error("Failed to export survey responses");

      const blob = await response.blob();
      const urlBlob = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = urlBlob;
      a.download = `survey-responses-${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(urlBlob);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error exporting survey responses:", error.message);
    }
  };

  const tableColumns = useMemo(
    () =>
      columns.map((column) => ({
        id: column.id,
        label: column.label,
        render: (row) => {
          if (column.id === "respondent") {
            return (
              <Box>
                <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>{row.name || "N/A"}</Typography>
                <Typography sx={{ ...typography.bodySmall, color: "#6C7C93" }}>{row.phone_no || ""}</Typography>
              </Box>
            );
          }
          if (column.id === "submitted_at") {
            return (
              <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
                {row.submitted_at ? new Date(row.submitted_at).toLocaleString() : "N/A"}
              </Typography>
            );
          }
          if (column.id === "status") {
            return (
              <Chip
                variant="outlined"
                color={row.status === "completed" ? "success" : "warning"}
                label={row.status_display || row.status || "N/A"}
                size="small"
              />
            );
          }
          return (
            <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>{row[column.id] || "N/A"}</Typography>
          );
        },
      })),
    [],
  );

  const renderExpanded = (row) => (
    <Box sx={{ p: 2, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 2 }}>
      <DetailItem label="Q1. Current Behaviour" value={row.current_behaviour} other={row.current_behaviour_other} />
      <DetailItem label="Q2. Reduce Reason" value={row.reduce_reason} />
      <DetailItem label="Q3. Specific Incident" value={row.specific_incident} />
      <DetailItem label="Q4. Resume Change" value={row.resume_change} other={row.resume_change_other} />
      <DetailItem label="Q5. Satisfaction" value={row.satisfaction} />
    </Box>
  );

  return (
    <Box>
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <TableSearchInput
            searchQuery={search}
            onSearchChange={(e) => updateSearch(e.target.value)}
            charCount={charCount}
            tooltip="Search by name or phone number"
            placeholder="Search by name or phone..."
          />
        </Box>

        <Select
          size="small"
          displayEmpty
          value={behaviourFilter}
          onChange={(e) => setBehaviourFilter(e.target.value)}
          sx={{ minWidth: 220, ...typography.bodyLarge }}
        >
          <MenuItem value="">All Q1 answers</MenuItem>
          {behaviourOptions.map((option) => (
            <MenuItem key={option.code} value={option.code}>
              {option.label}
            </MenuItem>
          ))}
        </Select>

        <Select
          size="small"
          displayEmpty
          value={satisfactionFilter}
          onChange={(e) => setSatisfactionFilter(e.target.value)}
          sx={{ minWidth: 200, ...typography.bodyLarge }}
        >
          <MenuItem value="">All Q5 answers</MenuItem>
          {satisfactionOptions.map((option) => (
            <MenuItem key={option.code} value={option.code}>
              {option.label}
            </MenuItem>
          ))}
        </Select>

        <ButtonOne
          variant="contained"
          onClick={handleExport}
          startIcon={<Download sx={{ fontSize: 18 }} />}
          sx={{
            ...typography.bodyLarge,
            backgroundColor: ColorsConstants.primaryPurple100,
            whiteSpace: "nowrap",
            "&:hover": { backgroundColor: ColorsConstants.primaryPurple100, opacity: 0.9 },
          }}
        >
          Export CSV
        </ButtonOne>
      </Box>

      <AppTable
        columns={tableColumns}
        rows={responses}
        rowKey={(row) => row.id}
        emptyMessage="No responses match these filters."
        count={rowCount}
        page={controller.page}
        rowsPerPage={controller.rowsPerPage}
        rowsPerPageOptions={[20, 50, 100]}
        onPageChange={(event, newPage) => setController((prev) => ({ ...prev, page: newPage }))}
        onRowsPerPageChange={(event) =>
          setController({ rowsPerPage: parseInt(event.target.value, 10), page: 0 })
        }
        renderExpanded={renderExpanded}
      />
    </Box>
  );
}

function DetailItem({ label, value, other }) {
  return (
    <Box>
      <Typography sx={{ ...typography.bodySmall, color: "#6C7C93" }}>{label}</Typography>
      <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
        {value || "N/A"}
        {other ? ` — "${other}"` : ""}
      </Typography>
    </Box>
  );
}
